import React, { useState } from "react";
import styled from "styled-components";
import {
  Paper,
  Button,
  Typography,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import useQuizReview from "../../../../swr/hooks/Library/useQuizReview";
import API from "../../../../axios/instances/API";
import { apiFormatDate, getSrcTwo } from "../../../../storage/helpers";
import { addDays } from "date-fns";
import { Alert } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { Calendar } from "react-feather";
import { DatePicker } from "@mui/x-date-pickers"; //TEST

const ModalWrapper = styled(Paper)`
  padding: 20px;
  width: calc(min(420px, 96vw));
  max-height: 400px;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  overflow-y: scroll;
  border: 2px solid black;
`;

const getErrorObj = (error) => {
  return {
    title: error?.response?.data?.error?.details || "Error",
    body:
      error?.response?.data?.error?.context?.start_date ||
      "There was an error updating the start date.",
  };
};

const EditStartDateModal = ({ id, setSnackbarText, handleClose }) => {
  const { details: data, isLoading } = useQuizReview(id);

  const [selectedDate, setSelectedDate] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);

  const handleUpdateStartDate = async () => {
    try {
      setUpdating(true);
      const sendObj = {
        id,
        due_date: data?.dueDate || null,
        start_date: apiFormatDate(selectedDate),
        name: data?.name,
        description: data?.description,
        estimated_time: data?.estimatedTime,
        language: data?.language,
        disclaimer: data?.disclaimer,
        disclaimer_text: data?.disclaimerText,
        display_answers: data?.displayAnswers,
      };

      await API.put("dashboard/quiz/details/", sendObj);
      setSnackbarText("Module start date updated success");
      return handleClose();
    } catch (error) {
      const errorObj = getErrorObj(error);
      setError(errorObj);
      setUpdating(false);
    }
  };

  if (isLoading) {
    return (
      <ModalWrapper>
        <div
          style={{
            width: "100%",
            height: "100%",
            minHeight: 160,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span style={{ fontSize: 12, color: "lightgray" }}>loading...</span>
        </div>
      </ModalWrapper>
    );
  }

  const minimumStartDate = addDays(new Date(), 1);

  return (
    <ModalWrapper>
      <div
        style={{
          width: "100%",
          minHeight: 140,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          justifyContent: "flex-start",
        }}
      >
        <div
          style={{
            height: 48,
            width: "100%",
            marginBottom: 24,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            borderRadius: 2,
            width: "100%",
          }}
        >
          <img
            alt="company"
            src={getSrcTwo(data.image)}
            style={{
              height: 46,
              width: 46,
              minHeight: 46,
              minWidth: 46,
              background: "white",
              border: "1px solid #eeeeee",
              borderRadius: 8,
              objectFit: "contain",
            }}
          />
          <div style={{ marginLeft: 6 }}>
            <Typography style={{ fontSize: 14, fontWeight: "600" }}>
              {data.name}
            </Typography>
            <Typography style={{ fontSize: 12 }}>
              {data?.marketPlaceParent?.company?.name}
            </Typography>
          </div>
        </div>
        <span style={{ fontWeight: "500", fontSize: 12, marginBottom: 4 }}>
          Select New Start Date
        </span>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            minDate={minimumStartDate}
            disableHighlightToday
            sx={{ width: "100%" }}
            open={open}
            color="primary"
            format="MMM dd yyyy"
            disablePast
            closeOnSelect
            onAccept={(date) => {
              setSelectedDate(date);
            }}
            onClose={() => setOpen(false)}
            slots={{
              inputAdornment: () => null,
            }}
            slotProps={{
              textField: {
                placeholder: " ",
                size: "small",
                onClick: () => setOpen(true),
                InputProps: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Calendar
                        style={{ height: 14, width: 14, color: "darkgrey" }}
                      />
                    </InputAdornment>
                  ),
                },
              },
            }}
          />
        </LocalizationProvider>
        <div style={{ marginBottom: 8, marginTop: 8, padding: 2 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
            }}
          >
            <span style={{ fontSize: 12, color: "darkgrey" }}>
              Module start date:
            </span>
            <span
              style={{
                fontSize: 12,
                color: "black",
                fontWeight: "500",
                marginLeft: 4,
              }}
            >
              {!data.startDate
                ? "None"
                : addDays(new Date(data.startDate), 1).toDateString()}
            </span>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              marginTop: 6,
            }}
          >
            <span style={{ fontSize: 12, color: "darkgrey" }}>
              Updated Module Start Date:
            </span>
            <span
              style={{
                fontSize: 12,
                color: "#337ab7",
                fontWeight: "500",
                marginLeft: 4,
              }}
            >
              {!selectedDate ? "---" : selectedDate.toDateString()}
            </span>
          </div>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            width: "100%",
          }}
        >
          <Button
            variant="contained"
            color="primary"
            size="small"
            disabled={!selectedDate || updating}
            onClick={handleUpdateStartDate}
            startIcon={
              updating ? (
                <CircularProgress size={16} sx={{ color: "black" }} />
              ) : null
            }
          >
            Update
          </Button>
        </div>
        {Boolean(error) && (
          <div style={{ width: "100%" }}>
            <div style={{ height: 8 }} />
            <Alert
              onClose={() => setError(null)}
              fullWidth
              severity="error"
              style={{
                width: "100%",
                minWidth: "100%",
                border: "1px solid firebrick",
              }}
            >
              <p style={{ fontWeight: "600" }}>{error?.title || "---"}</p>
              <p style={{ fontSize: 12 }}>{error?.body}</p>
              <div style={{ width: "100%" }} />
            </Alert>
          </div>
        )}
      </div>
    </ModalWrapper>
  );
};

export default EditStartDateModal;

{
  /* <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <DatePicker
            disableToolbar
            disablePast
            emptyLabel="Start Date"
            fullWidth
            // minDate={minimumStartDate}
            autoOk
            inputVariant="outlined"
            size="small"
            variant="inline"
            format="yyyy-MM-dd"
            value={selectedDate}
            onChange={(date) => setSelectedDate(date)}
            InputProps={{
              startAdornment: (
                <SelectedIndicator $isSelected={Boolean(selectedDate)} />
              ),
            }}
          />
        </MuiPickersUtilsProvider> */
}
